import _ from 'lodash';
import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {isTeammateNamespace} from '../../../../../../../libs/shared/core/src/models/helpers/namespaceHelpers';
import {Icon} from '../../../../../../../libs/shared/ui/src/components/icon/icon';
import {fontWeights} from '../../../../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../../../../libs/shared/ui/src/styles/mixins';
import {UserThemeConsumer} from '../../../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {EmojiDefinitionAndSkinTone} from '../../../../../../../srcCommon/helpers/emoji/emojiTextHelpers';
import {getEmojiHighlight} from '../../../../../../helpers/models/highlightHelpers';
import {TagModel} from '../../../../../../models/api/tagModel';
import {Emoji} from '../../../../../common/emoji/emoji';

/*
 * Props.
 */

interface ConversationHeaderTagPillTooltip {
  tagsPath: ReadonlyArray<TagModel>;
}

/*
 * Style.
 */

const StyledTooltipDiv = styled.div`
  display: flex;
  flex-wrap: wrap;
  max-width: 300px;
  row-gap: 3px;
`;

const StyledTokenDiv = styled.div`
  display: flex;
  align-items: center;
  ${ellipsis()}
`;

const StyledEmojiDiv = styled.div`
  margin-right: 5px;
`;

const StyledNameDiv = styled.div`
  font-weight: ${fontWeights.bold};
  ${ellipsis()}
`;

const StyledChevronDiv = styled.div`
  margin: 0 1px;
`;

const StyledLockDiv = styled.div`
  flex-shrink: 0;
  margin-left: 4px;
`;

const StyledTagDescriptionDiv = styled.div`
  margin-top: 9px;
  max-width: 300px;
  line-height: 18px;
  font-weight: ${fontWeights.normal};
  white-space: pre-wrap;
`;

const StyledPaddingDiv = styled.div`
  padding: 6px 4px;
`;

/*
 * Component.
 */

export const ConversationHeaderTagPillTooltip: FC<ConversationHeaderTagPillTooltip> = ({tagsPath}) => (
  <StyledPaddingDiv>
    <StyledTooltipDiv>
      {_(tagsPath)
        .slice()
        .reverse()
        .map((tag, index) => {
          const tagEmoji = getEmojiHighlight(tag);
          const isLast = index === tagsPath.length - 1;
          return (
            <StyledTokenDiv key={tag.id}>
              {tagEmoji && renderEmoji(tagEmoji)}
              <StyledNameDiv>{renderTagName(tag)}</StyledNameDiv>
              {!isLast && renderChevron()}
              {isLast && isTeammateNamespace(tag.namespace) && renderLock()}
            </StyledTokenDiv>
          );
        })
        .value()}
    </StyledTooltipDiv>
    {tagsPath[0].description && <StyledTagDescriptionDiv>{tagsPath[0].description}</StyledTagDescriptionDiv>}
  </StyledPaddingDiv>
);

function renderEmoji(emoji: EmojiDefinitionAndSkinTone) {
  return (
    <StyledEmojiDiv>
      <Emoji {...emoji} size={14} />
    </StyledEmojiDiv>
  );
}

function renderTagName(tag: TagModel) {
  // If this is the inbox tag, localize.
  if (tag.isMainInboxTag) {
    return <Trans id="tags-inboxtag">Inbox</Trans>;
  }

  // Otherwise, just use the name.
  return tag.name;
}

function renderChevron() {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledChevronDiv>
          <Icon name="chevronRightTiny" color={theme.greys.shade60} />
        </StyledChevronDiv>
      )}
    </UserThemeConsumer>
  );
}

function renderLock() {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledLockDiv>
          <Icon name="lockFilledTiny" color={theme.greys.shade50} />
        </StyledLockDiv>
      )}
    </UserThemeConsumer>
  );
}
